import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Divider,
  // Space,
  Card,
  Row,
  Col,
  message,
  notification,
} from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import configuration from 'modules/configuration'
import { connect } from 'react-redux'
import { generateSelectOptions } from 'modules/core/utils'
import { client } from 'httpclient'

const { TextArea } = Input

const subtypesWithReleaseStrategy = (documentType) =>
  (documentType?.subtypes ?? []).filter(
    (subtype) => subtype.requires_release_strategy ?? documentType?.requires_release_strategy,
  )

const CreateStrategyModal = ({
  visible,
  onCancel,
  mode = 'create',
  initialData,
  onRefetch,
  createStrategy,
  fetchAreasList,
  areaList,
  editAllStrategy,
  fetchUsersList,
  usersList,
  purchaseGroups,
  societies,
  isLoadingOptions,
}) => {
  const [isSaving, setIsSaving] = useState(false)
  const [documentTypes, setDocumentTypes] = useState([])
  const [isLoadingDocumentTypes, setIsLoadingDocumentTypes] = useState(false)
  const [form] = Form.useForm()
  const isEdit = mode === 'edit'
  const { t } = useTranslation()

  useEffect(() => {
    if (visible && isEdit && initialData && documentTypes.length > 0) {
      const docType = initialData?.document_type
      const documentTypeId =
        typeof docType === 'object' && docType?.id
          ? docType.id
          : documentTypes.find((dt) => dt.name === docType || dt.code === docType)?.id

      const dataAux = {
        ...initialData,
        area: initialData?.area ? initialData?.area?.id : undefined,
        society: initialData?.purchase_group ? initialData?.society?.id.toString() : undefined,
        purchase_group: initialData?.purchase_group
          ? initialData?.purchase_group?.id.toString()
          : undefined,
        document_type: documentTypeId,
        subtype_id: initialData?.conditions?.subtype_id,
        levels: initialData.levels.map((l) => {
          return {
            ...l,
            users: l.users.map((u) => u.id),
          }
        }),
      }
      form.setFieldsValue(dataAux)
    }

    if (!visible) {
      form.resetFields()
    }
  }, [visible, isEdit, initialData, form, documentTypes])

  useEffect(() => {
    if (!visible) return
    fetchAreasList()
    fetchUsersList()
  }, [visible, fetchAreasList, fetchUsersList])

  useEffect(() => {
    if (visible) {
      setIsLoadingDocumentTypes(true)
      client
        .get('/cnr-approvals/document-types/')
        .then((response) => {
          const data = response.data
          const items = Array.isArray(data) ? data : data?.results ?? data?.data ?? []
          setDocumentTypes(items)
        })
        .finally(() => setIsLoadingDocumentTypes(false))
    }
  }, [visible])

  const handleFinish = () => {
    form.validateFields().then((values) => {
      setIsSaving(true)

      const { subtype_id: subtypeId, ...rest } = values
      const dataAux = {
        ...rest,
        min_amount: values.min_amount ? parseInt(values.min_amount) : 0,
        max_amount: values.max_amount ? parseInt(values.max_amount) : null,
        ...(subtypeId && { conditions: { subtype_id: subtypeId } }),
      }

      if (!isEdit) {
        createStrategy(dataAux)
          .then(() => {
            onRefetch()
            message.success(t('FEEDBACK_CREATE_SUCCESS'), 8)
            setIsSaving(false)
            onClose()
          })
          .catch((error) => {
            notification.error({
              message: t('FEEDBACK_DEFAULT_ERROR'),
              description: error.response.data.message || t('FEEDBACK_CREATION_FAIL'),
              duration: 0,
            })
            setIsSaving(false)
            onClose()
          })
      } else {
        editAllStrategy(initialData.id, dataAux)
          .then(() => {
            onRefetch()
            message.success(t('FEEDBACK_SAVE_CHANGES_SUCCES'), 8)
            setIsSaving(false)
            onClose()
          })
          .catch((error) => {
            notification.error({
              message: t('FEEDBACK_DEFAULT_ERROR'),
              description: error.response.data.message || t('FEEDBACK_SAVE_CHANGES_FAIL'),
              duration: 0,
            })
            setIsSaving(false)
            onClose()
          })
      }
    })
  }

  const onClose = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      title={
        isEdit ? t('CONFIG_APPROVALS_EDIT_MODAL_TITLE') : t('CONFIG_APPROVALS_CREATE_MODAL_TITLE')
      }
      okText={isEdit ? t('ACTION_SAVE_CHANGES') : t('CONFIG_APPROVALS_CREATE_MODAL_ACTION')}
      okButtonProps={{ htmlType: 'submit', loading: isSaving, disabled: isSaving }}
      cancelButtonProps={{ disabled: isSaving }}
      cancelText={t('ACTION_CANCEL')}
      onOk={() => handleFinish()}
      destroyOnClose
      centered
      forceRender
      bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          levels: [],
        }}
      >
        {() => (
          <>
            <Form.Item
              label={t('CONFIG_APPROVALS_FORM_INPUT_LABEL')}
              name="name"
              rules={[{ required: true, message: t('ENTER_NAME_PLACEHOLDER') }]}
            >
              <Input placeholder={t('CONFIG_APPROVALS_FORM_INPUT_PLACEHOLDER')} />
            </Form.Item>

            <Row gutter={[4, 4]}>
              <Col span={24}>
                <Form.Item
                  label={t('LABEL_COMPANY')}
                  name="society"
                  rules={[{ required: true }]}
                  style={{ flex: 1 }}
                >
                  <Select
                    options={generateSelectOptions({
                      options: societies.map((dim) => {
                        return {
                          ...dim,
                          name: dim.name_destination,
                          code: dim.code_destination,
                        }
                      }),
                    })}
                    placeholder={t('ACTION_SELECT')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('CONFIG_APPROVALS_FORM_SELECT_DOCUMENT_TYPE_LABEL')}
                  name="document_type"
                  rules={[{ required: true }]}
                  style={{ flex: 1 }}
                >
                  <Select
                    options={generateSelectOptions({
                      options: Array.isArray(documentTypes) ? documentTypes : [],
                    })}
                    loading={isLoadingDocumentTypes}
                    placeholder={t('ACTION_SELECT')}
                    onChange={() => form.setFieldsValue({ subtype_id: undefined })}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                {form.getFieldsValue().document_type &&
                  documentTypes.find((dt) => dt.id === form.getFieldsValue().document_type)
                    ?.code === 'SAC' && (
                    <Form.Item
                      label={t('LABEL_AREA')}
                      name="area"
                      rules={[{ required: true }]}
                      style={{ flex: 1 }}
                    >
                      <Select
                        options={generateSelectOptions({ options: areaList })}
                        placeholder={t('ACTION_SELECT')}
                      />
                    </Form.Item>
                  )}
                {form.getFieldsValue().document_type &&
                  documentTypes.find((dt) => dt.id === form.getFieldsValue().document_type)
                    ?.code !== 'SAC' && (
                    <Form.Item
                      label={t('CONFIG_APPROVALS_PURCHASE_GROUP')}
                      name="purchase_group"
                      rules={[{ required: true }]}
                      style={{ flex: 1 }}
                    >
                      <Select
                        options={generateSelectOptions({
                          options: purchaseGroups.map((dim) => {
                            return {
                              ...dim,
                              name: dim.name_destination,
                              code: dim.code_destination,
                            }
                          }),
                        })}
                        loading={isLoadingOptions}
                        placeholder={t('ACTION_SELECT')}
                      />
                    </Form.Item>
                  )}
              </Col>
              {documentTypes.find((dt) => dt.id === form.getFieldsValue().document_type)
                ?.allows_manual_registration && (
                <Col span={12}>
                  <Form.Item
                    label={t('CONFIG_APPROVALS_SUBTYPE')}
                    name="subtype_id"
                    rules={[{ required: true }]}
                    style={{ flex: 1 }}
                  >
                    <Select
                      options={generateSelectOptions({
                        options: subtypesWithReleaseStrategy(
                          documentTypes.find((dt) => dt.id === form.getFieldsValue().document_type),
                        ).map((subtype) => {
                          return { id: subtype.id, name: subtype.name }
                        }),
                      })}
                      placeholder={t('ACTION_SELECT')}
                    />
                  </Form.Item>
                </Col>
              )}
              <Divider plain>{t('LABEL_RANGE')}</Divider>
              <Col span={12}>
                <Form.Item
                  label={t('CONFIG_APPROVALS_AMOUNT_MIN_COLUMN')}
                  name="min_amount"
                  style={{ flex: 1 }}
                >
                  <Input type="number" placeholder={t('ACTION_ENTER')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('CONFIG_APPROVALS_AMOUNT_MAX_COLUMN')}
                  name="max_amount"
                  style={{ flex: 1 }}
                >
                  <Input type="number" placeholder={t('ACTION_ENTER')} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label={t('FIELD_DESCRIPTION')} name="description">
              <TextArea
                rows={3}
                placeholder={t('CONFIG_APPROVALS_FORM_TEXAREA_DESCRIPTION_PLACEHOLDER')}
              />
            </Form.Item>

            <Divider />

            <h3>{t('CONFIG_APPROVALS_FORM_LEVELS_TEXT')}</h3>

            <Form.List name="levels">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Card
                      key={key}
                      style={{ marginBottom: 16 }}
                      bordered
                      size="small"
                      title={`${t('FIELD_LEVEL')} ${name + 1}`}
                      extra={
                        <DeleteOutlined
                          onClick={() => remove(name)}
                          style={{ color: 'red', cursor: 'pointer' }}
                        />
                      }
                    >
                      <Form.Item
                        {...rest}
                        name={[name, 'users']}
                        label={t('CONFIG_APPROVALS_FORM_SELECT_USERS_APPROVALS')}
                        rules={[{ required: true }]}
                      >
                        <Select
                          mode="multiple"
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.title.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          options={generateSelectOptions({
                            options: usersList.values.map((user) => {
                              return { ...user, name: user.full_name }
                            }),
                          })}
                          placeholder={t(
                            'CONFIG_APPROVALS_FORM_SELECT_USERS_APPROVALS_PLACEHOLDER',
                          )}
                        />
                      </Form.Item>
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    block
                    icon={<PlusOutlined />}
                    onClick={() =>
                      add({
                        users: [],
                      })
                    }
                  >
                    {t('CONFIG_APPROVALS_FORM_ADD_LEVEL_ACTION')}
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}
      </Form>
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  areaList: configuration.selectors.getAreaList(state),
  usersList: configuration.selectors.getUsersList(state),
})

const mapDispatchToProps = {
  createStrategy: configuration.actions.createStrategy,
  fetchAreasList: configuration.actions.fetchAreasList,
  editAllStrategy: configuration.actions.editAllStrategy,
  fetchUsersList: configuration.actions.fetchUsersList,
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateStrategyModal)
